import React, { useCallback, useMemo, useState } from "react";
import { PlainButton, TextInput } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { Button } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { ReactComponent as TextIcon } from "./assets/text.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { AgentKnowledgeContentHeader } from "../../../AgentKnowledgeContentHeader";
import { useAgentKnowledge } from "../../../../../../../../hooks/useAgentKnowledge";

export const TextKnowledgeForm: React.FC<{
  entityId: string;
  isDisabled: boolean;
  isFirstItem: boolean;
  editedKnowledge?: KnowledgeItem<"text">;
  onRequestClose: () => void;
}> = ({
  entityId,
  isFirstItem,
  isDisabled,
  editedKnowledge,
  onRequestClose,
}) => {
  const { t } = useTranslations();
  const { aiWorkflowsApi } = useRestApiProvider();
  const [title, setTitle] = useState(
    editedKnowledge?.data.metadata?.title || ""
  );
  const [text, setText] = useState(editedKnowledge?.data.source || "");
  const [isLoading, setIsLoading] = useState(false);

  const [uploadTextKnowledge] = aiWorkflowsApi.useUploadTextKnowledgeMutation();
  const [updateTextKnowledge] = aiWorkflowsApi.useUpdateTextKnowledgeMutation();
  const { handleCXApiError } = useAgentKnowledge(entityId);

  const handleCreateTextKnowledge = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = {
        agentId: entityId,
        entityId,
        content: text.trim(),
        title: title.trim(),
      };

      if (editedKnowledge) {
        await updateTextKnowledge({
          ...data,
          id: editedKnowledge.data.id,
        }).unwrap();
      } else {
        await uploadTextKnowledge(data).unwrap();
      }

      onRequestClose();
    } catch (error) {
      handleCXApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    editedKnowledge,
    entityId,
    handleCXApiError,
    onRequestClose,
    text,
    title,
    updateTextKnowledge,
    uploadTextKnowledge,
  ]);

  const headerTitle = useMemo(() => {
    if (editedKnowledge) {
      return t({
        id: "cx-ai-agents-page.edit-text-snippet",
        defaultMessage: "Edit Text Snippet",
      });
    }

    if (isFirstItem) {
      return t({
        id: "cx-ai-agents-page.text",
        defaultMessage: "Text",
      });
    }

    return t({
      id: "cx-ai-agents-page.new-text-snippet",
      defaultMessage: "New Text Snippet",
    });
  }, [editedKnowledge, isFirstItem, t]);

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <AgentKnowledgeContentHeader
        title={headerTitle}
        description={
          isFirstItem
            ? t({
                id: "cx-ai-agents-page.add-text-to-train-your-ai-agent",
                defaultMessage:
                  "Add plain text sources or instructions to train your AI Agent",
              })
            : undefined
        }
        icon={<TextIcon />}
        startContent={
          !isFirstItem ? (
            <PlainButton
              className="flex h-6 w-10 shrink-0 items-center justify-center"
              onClick={onRequestClose}
              disabled={isLoading}
            >
              <ArrowIcon />
            </PlainButton>
          ) : undefined
        }
      />
      <div className="jugl__custom-scrollbar my-8 flex justify-center overflow-y-auto px-10">
        <div className="flex w-full max-w-[800px] flex-col gap-10">
          <div className="flex flex-col gap-6">
            <TextInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-ai-agents-page.title",
                defaultMessage: "Title",
              })}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              value={title}
              onChange={(e) => setTitle(e.target.value.trimStart())}
              autoFocus
              isDisabled={isLoading || isDisabled}
            />
            <TextAreaInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-ai-agents-page.text",
                defaultMessage: "Text",
              })}
              placeholder={t({
                id: "cx-ai-agents-page.enter-text",
                defaultMessage: "Enter text",
              })}
              value={text}
              onChange={(e) => setText(e.target.value.trimStart())}
              classNames={{
                inputClassName: cx(
                  "min-h-[120px]",
                  (isLoading || isDisabled) && "cursor-not-allowed"
                ),
              }}
              isDisabled={isLoading || isDisabled}
            />
            <div className="ml-auto flex items-center gap-4">
              {editedKnowledge && !isDisabled && (
                <Button
                  variant="tertiary"
                  onClick={onRequestClose}
                  isDisabled={isLoading}
                >
                  {t({ id: "common.cancel", defaultMessage: "Cancel" })}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleCreateTextKnowledge}
                isDisabled={
                  !title.trim() || !text.trim() || isLoading || isDisabled
                }
              >
                {t({
                  id: "cx-ai-agents-page.save-text-snippet",
                  defaultMessage: "Save Text Snippet",
                })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
